// @givmo/app-admin — one occasion program's ACTIVITY (EP-BE8-web).
//
// The drill-in from a program row: every occasion the daily sweep has resolved for this program
// (`GET /admin/company/occasion-programs/{id}/executions`), filterable by status. Read-only — no money
// moves from this screen; it is where an admin finds out whether the gifts actually went out.
//
// ── HELD IS THE HEADLINE ─────────────────────────────────────────────────────────────────────────
//
// A HELD row is a gift the company OWES an employee and did not deliver. It is not an error state to
// be tidied away: the backend keeps the row (rather than deleting the claim, which is what the consumer
// birthday engine does) precisely so it stays visible, and every subsequent sweep retries it until it
// settles. So HELD leads the filter chips, carries the danger tone, and states its reason in words.
// `VELOCITY_CAP_REACHED` in particular is a NORMAL state — occasion spend shares the rolling 24-hour
// employer window with attended sends — and its copy says so rather than implying a defect.
//
// ── THE AMOUNT COLUMN IS THE ROW'S OWN PIN OR IT IS ABSENT ───────────────────────────────────────
//
// Each fence stores the price it was claimed at. A `PATCH` can re-price the program at any moment, so
// the program's CURRENT denomination is not what a past occurrence was charged, and a NULL pin means no
// price was ever committed — not "the current one". The cell is therefore omitted when the pin is null,
// never back-filled from the program. `formatExecutionPinnedAmount` takes the execution row and nothing
// else, so that substitution cannot be written here.
//
// ── DELIVERY-ONLY (D-EP-17) ──────────────────────────────────────────────────────────────────────
//
// Rows carry `gift_card_id` / `batch_id` so an admin can find the gift on the existing delivery
// surfaces, and carry NO redemption state. There is no "unclaimed" count here and none may be derived.

import { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery, type QueryFunctionContext } from '@tanstack/react-query';
import { sdk, isGivmoError, type CompanyOccasionExecutionStatus } from '@givmo/api-client';
import { toDateOnly } from '@givmo/api-client/list';
import {
  Badge,
  Button,
  Card,
  CardBody,
  DataTable,
  EmptyState,
  FilterBar,
  FilterChip,
  Icon,
  testIdProp,
  type DataTableColumn,
} from '@givmo/design-system';
import { AlertTriangle, ArrowLeft, ArrowRight, CalendarClock } from 'lucide-react';
import {
  EXECUTION_STATUS_FILTERS,
  executionStatusLabel,
  executionStatusTone,
  extractOccasionExecutions,
  formatExecutionPinnedAmount,
  heldReasonLabel,
  heldReasonShortLabel,
  isOccasionUnavailableError,
  occasionTypeLabel,
  PAUSE_FREEZES_HELD_COPY,
  type OccasionExecutionRow,
} from './occasion-programs-decode';
import { OccasionUnavailable } from './occasion-unavailable';

const PREFIX = 'gift-card-programs-execution';
const EXECUTIONS_PAGE_LIMIT = 20;

// Module-level: these cells close over nothing from the component, so the array can hold ONE
// identity for the app's lifetime and the memoized rows never re-render for a columns change.
const EXECUTION_COLUMNS: DataTableColumn<OccasionExecutionRow>[] = [
  {
    id: 'occasion',
    header: 'Occasion date',
    width: '160px',
    cell: (row) => (
      <div className="flex min-w-0 flex-col">
        <span className="truncate font-w600 text-text-strong">{row.occurrenceDate ?? '—'}</span>
        {row.occurrenceYear !== null && (
          <span className="text-s12 text-text-tertiary tabular-nums">{row.occurrenceYear}</span>
        )}
      </div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    width: '130px',
    cell: (row) => (
      <Badge tone={executionStatusTone(row.status)} dot testId={`${PREFIX}-status-${row.id}`}>
        {executionStatusLabel(row.status)}
      </Badge>
    ),
  },
  {
    id: 'reason',
    header: 'Why',
    width: '30%',
    cell: (row) =>
      row.status === 'HELD' || row.heldReason !== null ? (
        <span
          className="text-s13 text-text-secondary"
          {...testIdProp(`${PREFIX}-reason-${row.id}`)}
        >
          {heldReasonShortLabel(row.heldReason)}
        </span>
      ) : (
        <span className="text-text-tertiary">—</span>
      ),
  },
  {
    id: 'amount',
    header: 'Amount',
    width: '120px',
    // ★ The PINNED price only. A null pin renders NOTHING — never the program's current rule, which
    // would restate what an already-sent gift cost and drift under the admin after a re-price.
    cell: (row) => {
      const pinned = formatExecutionPinnedAmount(row);
      if (pinned === null) return <span className="text-text-tertiary">—</span>;
      return (
        <span
          className="tabular-nums text-text-strong"
          {...testIdProp(`${PREFIX}-amount-${row.id}`)}
        >
          {pinned}
        </span>
      );
    },
  },
  {
    id: 'attempts',
    header: 'Attempts',
    width: '100px',
    cell: (row) => <span className="tabular-nums text-text-secondary">{row.attempts ?? 0}</span>,
  },
  {
    id: 'sent',
    header: 'Sent',
    width: '120px',
    cell: (row) => <span className="text-text-secondary">{toDateOnly(row.executedAt) || '—'}</span>,
  },
];

export interface OccasionExecutionsViewProps {
  programId: string;
  /** Display context for the header — the program is a rule, so this names it without re-fetching. */
  occasionType: string | null | undefined;
  /** Whether the program is currently paused (changes what a held row means for the admin). */
  paused: boolean;
  onBack: () => void;
  /** Hand off to the Batches tab for a gift's delivery/charge detail. Omit to render no link. */
  onOpenBatch?: (batchId: string) => void;
}

export function OccasionExecutionsView({
  programId,
  occasionType,
  paused,
  onBack,
  onOpenBatch,
}: OccasionExecutionsViewProps) {
  const [status, setStatus] = useState<CompanyOccasionExecutionStatus | null>(null);

  const listKey = ['companies-list_company_occasion_executions', programId, status] as const;
  const list = useInfiniteQuery({
    queryKey: listKey,
    queryFn: async ({
      pageParam,
      signal,
    }: QueryFunctionContext<typeof listKey, number>): Promise<OccasionExecutionRow[]> => {
      const { data } = await sdk.companiesListCompanyOccasionExecutions({
        path: { program_id: programId },
        query: {
          'page[limit]': EXECUTIONS_PAGE_LIMIT,
          'page[offset]': pageParam,
          ...(status !== null ? { status } : {}),
        },
        signal,
        throwOnError: true,
      });
      return extractOccasionExecutions(data);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < EXECUTIONS_PAGE_LIMIT
        ? undefined
        : allPages.reduce((n, p) => n + p.length, 0),
    retry: false,
  });

  const rows = useMemo(() => (list.data ? list.data.pages.flat() : []), [list.data]);
  const heldOnPage = rows.filter((r) => r.status === 'HELD').length;

  // `renderExpanded` reaches the memoized row as a prop, so an inline arrow would re-render every
  // row on every parent render. Declared above the "unavailable" early return — a hook cannot sit
  // behind a conditional return.
  const renderExpanded = useCallback(
    (row: OccasionExecutionRow) => <ExecutionDetail row={row} onOpenBatch={onOpenBatch} />,
    [onOpenBatch],
  );

  const back = (
    <Button
      appearance="saas"
      tone="ghost"
      size="sm"
      leadingIcon={<Icon icon={ArrowLeft} size={16} aria-hidden />}
      onClick={onBack}
      testId={`${PREFIX}s-back`}
    >
      Back to programs
    </Button>
  );

  // The backend flag can be off in this environment even though the surface rendered — treat it as
  // "not available here", never as a failure of this program.
  if (list.isError && isOccasionUnavailableError(list.error)) {
    return (
      <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}s`)}>
        {back}
        <OccasionUnavailable testId={`${PREFIX}s-unavailable`} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}s`)}>
      {back}

      <Card>
        <CardBody className="flex flex-col gap-8">
          <h3 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
            {occasionTypeLabel(occasionType)} activity
          </h3>
          <p className="font-sans text-s13 leading-h15 tracking-givmo text-text-secondary">
            Every occasion this program has worked through. Gifts that could not be sent stay on
            this list and are retried automatically.
          </p>

          {/* A held row on a PAUSED program is frozen, not queued — say so where it is read. */}
          {paused && heldOnPage > 0 && (
            <div
              className="flex items-start gap-8 rounded-lg border border-warning-100 bg-warning-50 p-12"
              {...testIdProp(`${PREFIX}s-paused-held`)}
            >
              <Icon icon={AlertTriangle} size={16} aria-hidden className="mt-1 text-warning-700" />
              <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-warning-700">
                {PAUSE_FREEZES_HELD_COPY}
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      <DataTable
        testId={`${PREFIX}s-table`}
        columns={EXECUTION_COLUMNS}
        rows={rows}
        rowKey={(r) => r.id}
        rowTestId={(r) => `${PREFIX}-row-${r.id}`}
        // ★ The disclosure toggle must NOT share the row testid prefix: the rows are selected by
        // `[data-testid^="…-execution-row-"]`, and the default `${rowTestId}-expand` would be counted
        // as an extra row by that locator.
        expandTestId={(r) => `${PREFIX}-detail-${r.id}`}
        expandLabel={(r) => `Details for the occasion on ${r.occurrenceDate ?? 'this date'}`}
        renderExpanded={renderExpanded}
        loading={list.isPending}
        loadingTestId={`${PREFIX}s-loading`}
        error={
          list.isError
            ? isGivmoError(list.error)
              ? list.error.message
              : 'Failed to load this program’s activity.'
            : undefined
        }
        errorTestId={`${PREFIX}s-error`}
        retryTestId={`${PREFIX}s-error-retry`}
        onRetry={() => void list.refetch()}
        toolbar={
          <FilterBar aria-label="Filter occasions by status" testId={`${PREFIX}s-filters`}>
            {EXECUTION_STATUS_FILTERS.map((filter) => (
              <FilterChip
                key={filter.label}
                label={filter.label}
                selected={status === filter.value}
                onToggle={() => setStatus(filter.value)}
                testId={`${PREFIX}s-filter-${filter.label.toLowerCase()}`}
              />
            ))}
          </FilterBar>
        }
        emptyState={
          <EmptyState
            testId={`${PREFIX}s-empty-state`}
            icon={<Icon icon={CalendarClock} size={20} aria-hidden />}
            title={status === null ? 'Nothing has come up yet' : 'Nothing with that status'}
            description={
              status === null
                ? 'When an employee’s occasion comes around, the gift shows up here.'
                : 'Try another status filter.'
            }
          />
        }
        footer={
          list.hasNextPage ? (
            <>
              <span className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums">
                Showing {rows.length} occasions
              </span>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
                testId={`${PREFIX}s-load-more`}
              >
                {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </>
          ) : undefined
        }
      />
    </div>
  );
}

// ── Per-execution detail (the DataTable disclosure) ──────────────────────────────────────────────

function ExecutionDetail({
  row,
  onOpenBatch,
}: {
  row: OccasionExecutionRow;
  onOpenBatch?: (batchId: string) => void;
}) {
  const pinned = formatExecutionPinnedAmount(row);
  // Hoisted so the null check below narrows it for the click handler (a property read would widen
  // again inside the closure).
  const batchId = row.batchId;
  return (
    <div
      className="flex flex-col gap-8"
      {...testIdProp(`gift-card-programs-execution-body-${row.id}`)}
    >
      {(row.status === 'HELD' || row.heldReason !== null) && (
        <p className="font-sans text-s13 leading-h15 tracking-givmo text-text-secondary">
          {heldReasonLabel(row.heldReason)}
        </p>
      )}

      <dl className="grid grid-cols-2 gap-x-24 gap-y-6 font-sans text-s12 tracking-givmo sm:grid-cols-3">
        <Fact label="Attempts" value={String(row.attempts ?? 0)} />
        {/* Omitted entirely when the row carries no pin — an unknown price is not a zero. */}
        {pinned !== null && <Fact label="Gift amount" value={pinned} />}
        <Fact label="First seen" value={toDateOnly(row.createdAt) || '—'} />
        {row.giftCardId !== null && (
          <Fact
            label="Gift card"
            value={row.giftCardId}
            testId={`gift-card-programs-execution-card-${row.id}`}
          />
        )}
      </dl>

      {/* The batch is the charge + receipt grain, and its detail already renders the charged totals —
          so this hands off to that existing surface rather than restating money here. */}
      {batchId !== null && onOpenBatch !== undefined && (
        <div className="flex flex-wrap items-center gap-8">
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            trailingIcon={<Icon icon={ArrowRight} size={14} aria-hidden />}
            onClick={() => onOpenBatch(batchId)}
            testId={`gift-card-programs-execution-batch-${row.id}`}
          >
            View the batch this gift was sent in
          </Button>
          <span className="font-sans text-s12 tracking-givmo text-text-tertiary">
            Delivery details live on the Batches tab.
          </span>
        </div>
      )}
    </div>
  );
}

function Fact({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div className="flex min-w-0 flex-col">
      <dt className="font-w600 text-text-tertiary">{label}</dt>
      <dd className="truncate text-text-strong tabular-nums" {...testIdProp(testId)}>
        {value}
      </dd>
    </div>
  );
}
